import { useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import styles from './GradingInfo.module.css';

const gradingKeys = ['M', 'NM', 'VG_PLUS', 'VG', 'G_PLUS', 'G', 'F'];
const dimensionsKeys = ['A0', 'A1', 'A2', 'A3', '50x70', '60x90', 'Custom'];

/**
 * Displays a grading or dimensions guide in a modal.
 *
 * @component
 * @param {Object} props
 * @param {'grading'|'dimensions'} props.type guide content type
 * @param {string?} props.customValue custom dimensions label
 * @param {string?} props.customDescription custom dimensions description
 * @param {boolean} props.hideCustomGenericOption hide the generic custom-size entry
 * @param {string?} props.triggerClassName optional custom trigger styling
 * @param {React.ReactNode?} props.triggerContent optional custom trigger content
 * @returns {JSX.Element}
 */
export default function GradingInfo({
  type = 'grading',
  customValue = null,
  customDescription = null,
  hideCustomGenericOption = false,
  triggerClassName = null,
  triggerContent = null,
}) {
  const [open, setOpen] = useState(false);
  const intl = useIntl();
  const portalRoot =
    typeof document !== 'undefined' ? document.getElementById('portal-root') || document.body : null;

  const isDimensions = type === 'dimensions';
  const keys = isDimensions ? dimensionsKeys : gradingKeys;
  const guidePrefix = isDimensions ? 'dimensions' : 'grading';

  const openLabel = intl.formatMessage({
    id: isDimensions ? 'GradingInfo_dimensionsGuide_openLabel' : 'GradingInfo_gradingGuide_openLabel',
    defaultMessage: isDimensions ? 'Open dimensions guide' : 'Open grading guide',
  });

  const openFromEvent = e => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        className={triggerClassName || styles.iconButton}
        onPointerUp={openFromEvent}
        onTouchEnd={openFromEvent}
        onClick={openFromEvent}
        aria-label={openLabel}
        title={openLabel}
      >
        {triggerContent || 'ⓘ'}
      </button>

      {open && portalRoot
        ? ReactDOM.createPortal(
            <div
              className={styles.overlay}
              role="presentation"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
              }}
            >
              <div
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="GradingInfoTitle"
                onClick={e => e.stopPropagation()}
              >
                <div className={styles.modalInner}>
                  {/* Header */}
                  <div className={styles.header}>
                    <h3 id="GradingInfoTitle">
                      {intl.formatMessage({
                        id: isDimensions ? 'GradingInfo_dimensionsGuide_title' : 'GradingInfo_gradingGuide_title',
                        defaultMessage: isDimensions ? 'Dimensions Guide' : 'Grading Guide',
                      })}
                    </h3>

                    <button
                      className={styles.close}
                      onClick={() => setOpen(false)}
                      type="button"
                      aria-label={intl.formatMessage({
                        id: 'GradingInfo_gradingGuide_closeLabel',
                        defaultMessage: 'Close',
                      })}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Content */}
                  <div className={styles.content}>
                    {(customDescription || customValue) && (
                      <div className={styles.card}>
                        <div className={styles.cardHeader}>
                          {customValue ? customValue : intl.formatMessage({
                            id: 'CustomExtendedDataField.customDimensionsDescriptionLabel',
                            defaultMessage: 'Custom Size Description',
                          })}
                        </div>
                        {customDescription && (
                          <div className={styles.cardBody}>
                            {customDescription}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {keys
                      .filter(key => !(hideCustomGenericOption && key === 'Custom'))
                      .map(key => (
                      <div key={key} className={styles.card}>
                        <div className={styles.cardHeader}>
                          {intl.formatMessage({
                            id: `GradingInfo_${guidePrefix}_${key}_Label`,
                          })}
                        </div>

                        <div className={styles.cardBody}>
                          {intl.formatMessage({
                            id: `GradingInfo_${guidePrefix}_${key}_Description`,
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>,
            portalRoot
          )
        : null}
    </>
  );
}
